import React, { useState } from "react";
import { S, buildAffiliateUrl } from "./styles";

export function WelcomeOverlay({ onDone, onStory }) {
  // Single, impact-led welcome — the full 3-step tour lives in Profile → "App tour"
  // so first paint stays light and respectful of the shopper's attention.
  return (
    <div style={S.modalOverlay} data-testid="welcome-overlay">
      <div style={{ background: "white", borderRadius: 24, padding: 36, maxWidth: 440, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>🗺️</div>
          <div style={{ ...S.fraunces, fontSize: 26, fontWeight: 700, marginBottom: 10, color: "#0F3D2E" }}>Welcome to Mapbazar</div>
          <div style={{ fontSize: 14, color: "#555", lineHeight: 1.6 }}>
            Local shops on a map · 30-min delivery · health rewards on every order.
          </div>
        </div>
        <button data-testid="welcome-next-btn" onClick={onDone} style={{ width: "100%", padding: 14, borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
          Enter Mapbazar
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
          <button onClick={onStory} style={{ background: "none", border: "none", color: "#666", fontSize: 12, cursor: "pointer" }}>Why Mapbazar?</button>
          <button onClick={onDone} data-testid="welcome-skip-btn" style={{ background: "none", border: "none", color: "#999", fontSize: 12, cursor: "pointer" }}>Skip</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Full 3-slide intro tour — moved out of first-paint and into Profile so users
 * can replay it on demand without it being shoved in their face on every fresh
 * session.
 */
export function AppTourOverlay({ onClose }) {
  const [step, setStep] = useState(0);
  const slides = [
    { icon: "🗺️", t: "Map Local Stores", d: "Use our AI agent to see real-time stock near you on mapbazar.com.", c: "#0F3D2E" },
    { icon: "🛵", t: "30-Minute Delivery", d: "Skip the traffic, save petrol, and get your items delivered home fast.", c: "#C97B3C" },
    { icon: "🌱", t: "Earn Health Rewards", d: "Unlock free AI preventive HealthPoints with every single purchase.", c: "#8B1A4A" },
  ];
  const s = slides[step];
  return (
    <div style={S.modalOverlay} data-testid="app-tour-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: 24, padding: 36, maxWidth: 440, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>{s.icon}</div>
          <div style={{ ...S.fraunces, fontSize: 26, fontWeight: 700, marginBottom: 10, color: s.c }}>{s.t}</div>
          <div style={{ fontSize: 14, color: "#555", lineHeight: 1.6 }}>{s.d}</div>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 20 }}>
          {slides.map((_, i) => (<div key={i} style={{ width: i === step ? 24 : 8, height: 8, borderRadius: 4, background: i === step ? s.c : "#E0DED2", transition: "all 0.3s" }} />))}
        </div>
        <button data-testid="app-tour-next-btn" onClick={() => (step < slides.length - 1 ? setStep(step + 1) : onClose())} style={{ width: "100%", padding: 14, borderRadius: 100, border: "none", background: s.c, color: "white", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
          {step < slides.length - 1 ? "Next →" : "Done"}
        </button>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button onClick={onClose} data-testid="app-tour-close-btn" style={{ background: "none", border: "none", color: "#999", fontSize: 12, cursor: "pointer" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

export function StoryModal({ onClose }) {
  return (
    <div style={S.modalOverlay} data-testid="story-modal">
      <div style={{ background: "white", borderRadius: 24, padding: 32, maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ fontSize: 11, letterSpacing: 2, color: "#C97B3C", fontWeight: 700, marginBottom: 8 }}>A NOTE FROM KIRIT · FOUNDER</div>
        <div style={{ ...S.fraunces, fontSize: 24, fontWeight: 700, letterSpacing: "-0.5px", marginBottom: 18, lineHeight: 1.2 }}>Something I kept seeing behind the pharmacy counter.</div>
        <div style={{ fontSize: 14, color: "#333", lineHeight: 1.75 }}>
          <p style={{ marginBottom: 14 }}>I'm a pharmacist. For ten years I stood behind a counter in India.</p>
          <p style={{ marginBottom: 14 }}>A young man would walk in after buying <strong>₹10,000 sneakers</strong>. Twenty minutes later, his father would ask for cheap antacids — and refuse the <strong>₹500 health check</strong> his doctor had recommended for months.</p>
          <p style={{ marginBottom: 14 }}>It wasn't a money problem. The family had money. It was that everyday spending feels automatic, and routine health checks feel optional — until one day they're not.</p>
          <div style={{ padding: 18, background: "#FFF9F0", borderLeft: "3px solid #C97B3C", borderRadius: 10, marginBottom: 18 }}>
            <p style={{ fontWeight: 600, color: "#5C3D12", fontStyle: "italic" }}>So I built Mapbazar. Every time that son shops through our partners, rewards quietly build up — and they pay our pharmacists and doctors who look after his family's preventive care.</p>
          </div>
          <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 700, color: "#0F3D2E", marginTop: 22, marginBottom: 10 }}>How the model works</div>
          <p style={{ marginBottom: 12 }}>Our partners — Amazon, Flipkart, Swiggy, and others — pay us a small affiliate commission on every purchase. Most of that becomes Health Credits in your account.</p>
          <p style={{ marginBottom: 18, fontWeight: 600, color: "#0F3D2E" }}>Your shopping pays our doctors. Our doctors look after your family.</p>
          <p style={{ marginTop: 14, fontStyle: "italic", color: "#666" }}>— Kirit<br/>Founder · Pharmacist</p>
        </div>
        <button data-testid="story-close-btn" onClick={onClose} style={{ marginTop: 24, padding: 14, borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, width: "100%", cursor: "pointer" }}>Got it</button>
      </div>
    </div>
  );
}

export function WhyModal({ onClose }) {
  return (
    <div style={S.modalOverlay} data-testid="why-modal">
      <div style={{ background: "white", borderRadius: 24, padding: 32, maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ fontSize: 11, letterSpacing: 2, color: "#C97B3C", fontWeight: 700, marginBottom: 8 }}>HOW REWARDS FUND CARE</div>
        <div style={{ ...S.fraunces, fontSize: 26, fontWeight: 700, letterSpacing: "-0.5px", marginBottom: 20, lineHeight: 1.2 }}>Your shopping pays our doctors.</div>
        <div style={{ fontSize: 14, color: "#333", lineHeight: 1.7 }}>
          <p style={{ marginBottom: 14 }}>When you shop through Mapbazar, our partners pay us a small affiliate commission on every purchase.</p>
          <p style={{ marginBottom: 14 }}>About 90% becomes Health Credits in your account. A small portion shows up as bonus credits or surprise cashback — variety keeps things interesting.</p>
          <p style={{ marginBottom: 14 }}>Those credits pay our in-house pharmacists and doctors. They look after your family's preventive wellness. AI assists them by watching for patterns — but real doctors make the calls.</p>
          <div style={{ padding: 18, background: "#F0F7F2", borderLeft: "3px solid #0F3D2E", borderRadius: 10, marginBottom: 14 }}>
            <p style={{ fontWeight: 700, color: "#0F3D2E", fontSize: 15, lineHeight: 1.5 }}>Your shopping pays our doctors.<br/>Our doctors look after your family.</p>
          </div>
          <p style={{ fontStyle: "italic", color: "#666" }}>No scary reminders. No disease pressure. Just a gentle system of care, quietly running in the background.</p>
        </div>
        <button data-testid="why-close-btn" onClick={onClose} style={{ marginTop: 24, padding: 14, borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, width: "100%", cursor: "pointer" }}>Makes sense</button>
      </div>
    </div>
  );
}

export function BrowserModal({ browser, onComplete, onClose }) {
  const { partner, query } = browser;
  return (
    <div style={S.modalOverlay} data-testid="browser-modal">
      <div style={{ background: "white", borderRadius: 20, maxWidth: 440, width: "100%", overflow: "hidden", display: "flex", flexDirection: "column", height: "75vh", maxHeight: 600 }}>
        <div style={{ background: partner.color, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", color: "white" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>{partner.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{partner.name}</div>
              <div style={{ fontSize: 10, opacity: 0.85 }}>Via Mapbazar · {partner.cb} back</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="browser-close-btn" style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%", width: 28, height: 28, color: "white", fontSize: 14, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ flex: 1, background: "#F8F8F5", padding: 24, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>{partner.icon}</div>
          <div style={{ ...S.fraunces, fontSize: 19, fontWeight: 700, marginBottom: 8 }}>Shopping "{query}"</div>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 24, maxWidth: 300, lineHeight: 1.6 }}>Thanks to {partner.name} — our partner. Shop as you normally would.</div>
          <div style={{ padding: 16, background: "white", borderRadius: 12, marginBottom: 20, border: "1px dashed #D5D3C7", fontSize: 11, color: "#888", maxWidth: 320, lineHeight: 1.5 }}>You'll be redirected to {partner.name} with your Mapbazar affiliate tag. 100% of commission funds family preventive check-ups.</div>
          <a data-testid="browser-affiliate-btn" href={buildAffiliateUrl(partner, query)} target="_blank" rel="noopener noreferrer" onClick={() => onComplete && onComplete(partner, query)} style={{ padding: "12px 24px", borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, cursor: "pointer", textDecoration: "none" }}>Shop on {partner.name} →</a>
        </div>
      </div>
    </div>
  );
}

export function BookingModal({ pkg, credits, member, onSetReminder, onClose }) {
  const [reminderDate, setReminderDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  return (
    <div style={S.modalOverlay} data-testid="booking-modal">
      <div style={{ background: "white", borderRadius: 24, padding: 28, maxWidth: 460, width: "100%" }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: "#0F3D2E", fontWeight: 700, marginBottom: 6 }}>ROUTINE CHECK-IN · GUIDE</div>
        <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{pkg.name}</div>
        <div style={{ fontSize: 12.5, color: "#666", marginBottom: 18 }}>For {member?.name || "—"} · {pkg.description}</div>
        <div style={{ background: "#F0EFE8", borderRadius: 12, padding: 16, marginBottom: 18 }}>
          <Row l="Typical market price" v={`₹${pkg.price}`} strike />
          <Row l="Recommended every" v={pkg.frequency || "6–12 months"} />
        </div>
        <div style={{ fontSize: 12, color: "#666", padding: 14, background: "#FFF9F0", border: "1px solid #F3CC8B", borderRadius: 10, marginBottom: 14, lineHeight: 1.55 }}>
          This is a guide to what we recommend. Set a reminder and we'll nudge {member?.name || "you"} when it's due. Add to your calendar in one tap.
        </div>
        <label style={{ display: "block", fontSize: 11, letterSpacing: 1.2, color: "#555", fontWeight: 700, marginBottom: 6 }}>REMIND ME ON</label>
        <input data-testid="reminder-date-input" type="date" value={reminderDate} min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setReminderDate(e.target.value)}
          style={{ width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #E0DED2", fontSize: 13, outline: "none", background: "#FAFAF5", marginBottom: 16 }} />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 13, borderRadius: 100, border: "1px solid #D5D3C7", background: "white", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button onClick={() => onSetReminder && onSetReminder(pkg, reminderDate)} data-testid="reminder-set-btn" style={{ flex: 2, padding: 13, borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, cursor: "pointer" }}>
            🔔 Set up reminder
          </button>
        </div>
      </div>
    </div>
  );
}

export function ReceiptModal({ onClose, onSubmit }) {
  const [merchant, setMerchant] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const ip = { width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #E0DED2", fontSize: 13, outline: "none", background: "#FAFAF5" };
  const ok = merchant && amount && date;
  return (
    <div style={S.modalOverlay} data-testid="receipt-modal">
      <div style={{ background: "white", borderRadius: 24, padding: 28, maxWidth: 420, width: "100%" }}>
        <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Upload receipt</div>
        <div style={{ fontSize: 13, color: "#666", marginBottom: 18 }}>Shopped at a local shop? Tell us the basics — our team will verify and credit your rewards.</div>
        <div style={{ display: "grid", gap: 12 }}>
          <div><div style={{ fontSize: 10, letterSpacing: 1.5, color: "#666", fontWeight: 600, marginBottom: 5 }}>MERCHANT</div><input data-testid="receipt-merchant" style={ip} value={merchant} onChange={(e) => setMerchant(e.target.value)} placeholder="Shah Kirana" /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><div style={{ fontSize: 10, letterSpacing: 1.5, color: "#666", fontWeight: 600, marginBottom: 5 }}>AMOUNT (₹)</div><input data-testid="receipt-amount" type="number" style={ip} value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
            <div><div style={{ fontSize: 10, letterSpacing: 1.5, color: "#666", fontWeight: 600, marginBottom: 5 }}>DATE</div><input data-testid="receipt-date" type="date" style={ip} value={date} onChange={(e) => setDate(e.target.value)} /></div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 13, borderRadius: 100, border: "1px solid #D5D3C7", background: "white", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button data-testid="receipt-submit-btn" onClick={() => ok && onSubmit({ merchant, amount: parseFloat(amount), date })} disabled={!ok} style={{ flex: 2, padding: 13, borderRadius: 100, border: "none", background: ok ? "#0F3D2E" : "#CCC", color: "white", fontWeight: 700, cursor: ok ? "pointer" : "not-allowed" }}>Submit receipt</button>
        </div>
      </div>
    </div>
  );
}

export function AddMemberForm({ onSubmit, onCancel, preset }) {
  const [f, setF] = useState({
    name: "",
    relation: preset?.relation || "father",
    age: "",
    sex: preset?.sex || "male",
    smoker: "no",
    conditions: "",
    height_cm: "", weight_kg: "", primary_focus: "general",
  });
  const ip = { width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #E0DED2", fontSize: 13, outline: "none", background: "#FAFAF5" };
  const bmi = (f.height_cm && f.weight_kg) ? (parseFloat(f.weight_kg) / Math.pow(parseFloat(f.height_cm) / 100, 2)).toFixed(1) : null;
  return (
    <div style={S.modalOverlay} data-testid="add-member-modal">
      <div style={{ background: "white", borderRadius: 24, padding: 28, maxWidth: 420, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Add family member</div>
        <div style={{ fontSize: 12.5, color: "#666", marginBottom: 18 }}>Just the basics. You can fill in lifestyle & medical background later.</div>
        <div style={{ display: "grid", gap: 12 }}>
          <Field l="Name"><input data-testid="member-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Rajesh" style={ip} /></Field>
          <Field l="Relation">
            <select data-testid="member-relation" value={f.relation} onChange={(e) => setF({ ...f, relation: e.target.value })} style={ip}>
              {["father", "mother", "spouse", "son", "daughter", "self", "other"].map(r => <option key={r} value={r} style={{ textTransform: "capitalize" }}>{r}</option>)}
            </select>
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field l="Age"><input data-testid="member-age" type="number" value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} style={ip} /></Field>
            <Field l="Sex">
              <select data-testid="member-sex" value={f.sex} onChange={(e) => setF({ ...f, sex: e.target.value })} style={ip}>
                <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
              </select>
            </Field>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field l="Height (cm)"><input data-testid="member-height" type="number" value={f.height_cm} onChange={(e) => setF({ ...f, height_cm: e.target.value })} placeholder="170" style={ip} /></Field>
            <Field l="Weight (kg)"><input data-testid="member-weight" type="number" value={f.weight_kg} onChange={(e) => setF({ ...f, weight_kg: e.target.value })} placeholder="65" style={ip} /></Field>
          </div>
          {bmi && (
            <div data-testid="member-bmi-hint" style={{ padding: "8px 12px", borderRadius: 10, background: "#F6FAF7", border: "1px solid #CFE1D5", fontSize: 11.5, color: "#0F3D2E" }}>
              BMI ≈ <strong>{bmi}</strong> — Ayu will use this to tailor preventive guidance.
            </div>
          )}
          <Field l="Primary wellness focus">
            <select data-testid="member-focus" value={f.primary_focus} onChange={(e) => setF({ ...f, primary_focus: e.target.value })} style={ip}>
              <option value="general">General fitness</option>
              <option value="sleep">Better sleep</option>
              <option value="energy">More energy</option>
              <option value="stress">Stress reduction</option>
              <option value="fitness">Athletic fitness</option>
            </select>
          </Field>
          <Field l="Known conditions (optional, doctor-diagnosed)"><input data-testid="member-conditions" value={f.conditions} onChange={(e) => setF({ ...f, conditions: e.target.value })} placeholder="e.g. On BP medication (comma-separated)" style={ip} /></Field>
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: "#888", lineHeight: 1.5 }}>
          You'll be able to add lifestyle habits and family history from the Overview tab — Ayu prompts you before your first Deep Review.
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: 13, borderRadius: 100, border: "1px solid #D5D3C7", background: "white", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button data-testid="member-save-btn" onClick={() => f.name && f.age && onSubmit(f)} style={{ flex: 2, padding: 13, borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, cursor: "pointer" }}>Add member</button>
        </div>
      </div>
    </div>
  );
}

function Row({ l, v, strike, hl, big }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: big ? 15 : 12.5 }}>
      <span style={{ color: hl ? "#0F3D2E" : "#666", fontWeight: hl ? 600 : 500 }}>{l}</span>
      <span style={{ color: hl ? "#0F3D2E" : big ? "#0F3D2E" : "#999", fontWeight: hl || big ? 700 : 500, textDecoration: strike ? "line-through" : "none" }}>{v}</span>
    </div>
  );
}

function Field({ l, children }) {
  return (<div><div style={{ fontSize: 10, letterSpacing: 1.5, color: "#666", fontWeight: 600, marginBottom: 5 }}>{l.toUpperCase()}</div>{children}</div>);
}

const FAMILY_HIST_OPTS = [
  { k: "heart", l: "Heart disease" },
  { k: "diabetes", l: "Diabetes" },
  { k: "cancer", l: "Cancer" },
  { k: "stroke", l: "Stroke" },
  { k: "bp", l: "High blood pressure" },
  { k: "cholesterol", l: "High cholesterol" },
];

const DX_OPTS = [
  { k: "bp", l: "High blood pressure" },
  { k: "cholesterol", l: "High cholesterol" },
  { k: "pre_diabetes", l: "Pre-diabetes" },
  { k: "diabetes", l: "Diabetes" },
  { k: "thyroid", l: "Thyroid condition" },
  { k: "asthma", l: "Asthma" },
];

/** Health Profile editor — Tier 2 (lifestyle) + Tier 3 (medical background).
 *  Called from Overview tab or as a gate before the first Deep Review. */
export function HealthProfileModal({ member, onSave, onClose }) {
  const [f, setF] = useState({
    height_cm: member.height_cm || "",
    weight_kg: member.weight_kg || "",
    primary_focus: member.primary_focus || "general",
    activity_level: member.activity_level || "",
    smoking: member.smoking || "",
    alcohol: member.alcohol || "",
    sleep_hours: member.sleep_hours || "",
    family_history: new Set(member.family_history || []),
    existing_diagnoses: new Set(member.existing_diagnoses || []),
    medications: (member.medications || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const ip = { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0DED2", fontSize: 13, outline: "none", background: "#FAFAF5" };
  const bmi = (f.height_cm && f.weight_kg) ? (parseFloat(f.weight_kg) / Math.pow(parseFloat(f.height_cm) / 100, 2)).toFixed(1) : null;

  const toggleTag = (key, val) => setF((x) => {
    const s = new Set(x[key]);
    if (s.has(val)) s.delete(val); else s.add(val);
    return { ...x, [key]: s };
  });

  const save = async () => {
    setSaving(true);
    await onSave({
      height_cm: f.height_cm ? parseFloat(f.height_cm) : null,
      weight_kg: f.weight_kg ? parseFloat(f.weight_kg) : null,
      primary_focus: f.primary_focus || null,
      activity_level: f.activity_level || null,
      smoking: f.smoking || null,
      alcohol: f.alcohol || null,
      sleep_hours: f.sleep_hours ? parseFloat(f.sleep_hours) : null,
      family_history: Array.from(f.family_history),
      existing_diagnoses: Array.from(f.existing_diagnoses),
      medications: f.medications ? f.medications.split(",").map((x) => x.trim()).filter(Boolean) : [],
    });
    setSaving(false);
  };

  return (
    <div style={S.modalOverlay} data-testid="health-profile-modal">
      <div style={{ background: "white", borderRadius: 24, padding: 24, maxWidth: 480, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{member.name}'s health profile</div>
        <div style={{ fontSize: 12.5, color: "#666", marginBottom: 18, lineHeight: 1.55 }}>
          The more Ayu knows, the sharper your Deep Review for the doctor. All fields optional — answer what you're comfortable sharing.
        </div>

        {/* Tier 1 basics */}
        <SectionHeader>Basics</SectionHeader>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <Field l="Height (cm)"><input data-testid="profile-height" type="number" value={f.height_cm} onChange={(e) => setF({ ...f, height_cm: e.target.value })} style={ip} /></Field>
          <Field l="Weight (kg)"><input data-testid="profile-weight" type="number" value={f.weight_kg} onChange={(e) => setF({ ...f, weight_kg: e.target.value })} style={ip} /></Field>
        </div>
        {bmi && (
          <div style={{ padding: "8px 12px", borderRadius: 10, background: "#F6FAF7", border: "1px solid #CFE1D5", fontSize: 11.5, color: "#0F3D2E", marginBottom: 12 }}>
            BMI ≈ <strong>{bmi}</strong>
          </div>
        )}
        <Field l="Primary focus">
          <select data-testid="profile-focus" value={f.primary_focus} onChange={(e) => setF({ ...f, primary_focus: e.target.value })} style={ip}>
            <option value="general">General fitness</option>
            <option value="sleep">Better sleep</option>
            <option value="energy">More energy</option>
            <option value="stress">Stress reduction</option>
            <option value="fitness">Athletic fitness</option>
          </select>
        </Field>

        {/* Tier 2 lifestyle */}
        <SectionHeader style={{ marginTop: 18 }}>Everyday lifestyle</SectionHeader>
        <Field l="Daily movement">
          <select data-testid="profile-activity" value={f.activity_level} onChange={(e) => setF({ ...f, activity_level: e.target.value })} style={ip}>
            <option value="">—</option>
            <option value="sedentary">Mostly at a desk / sedentary</option>
            <option value="light">Light movement a few days a week</option>
            <option value="moderate">Active most days (walks, errands)</option>
            <option value="very_active">Very active (workout / on feet all day)</option>
          </select>
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
          <Field l="Smoking / tobacco">
            <select data-testid="profile-smoking" value={f.smoking} onChange={(e) => setF({ ...f, smoking: e.target.value })} style={ip}>
              <option value="">—</option>
              <option value="never">Never</option>
              <option value="former">Former user</option>
              <option value="occasional">Occasional</option>
              <option value="daily">Daily</option>
            </select>
          </Field>
          <Field l="Alcohol">
            <select data-testid="profile-alcohol" value={f.alcohol} onChange={(e) => setF({ ...f, alcohol: e.target.value })} style={ip}>
              <option value="">—</option>
              <option value="never">Never</option>
              <option value="rare">Rarely (&lt;1×/mo)</option>
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
          </Field>
        </div>
        <div style={{ marginTop: 10 }}>
          <Field l="Typical sleep (hours/night)"><input data-testid="profile-sleep" type="number" step="0.5" value={f.sleep_hours} onChange={(e) => setF({ ...f, sleep_hours: e.target.value })} placeholder="e.g. 7" style={ip} /></Field>
        </div>

        {/* Tier 3 medical background */}
        <SectionHeader style={{ marginTop: 18 }}>Medical background</SectionHeader>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 8, lineHeight: 1.5 }}>
          <strong>Family history</strong> — anyone in parents/siblings diagnosed before age 60?
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }} data-testid="profile-family-history">
          {FAMILY_HIST_OPTS.map((o) => {
            const on = f.family_history.has(o.k);
            return (
              <button key={o.k} data-testid={`profile-family-${o.k}`} onClick={() => toggleTag("family_history", o.k)}
                style={{
                  padding: "6px 12px", borderRadius: 100, border: on ? "none" : "1px solid #D5D3C7",
                  background: on ? "#0F3D2E" : "white", color: on ? "white" : "#333",
                  fontSize: 11.5, fontWeight: on ? 700 : 500, cursor: "pointer",
                }}>
                {on ? "✓ " : ""}{o.l}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 8, lineHeight: 1.5 }}>
          <strong>Existing diagnoses</strong> — has a doctor told {member.name.split(" ")[0]} they have any of these?
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }} data-testid="profile-existing-dx">
          {DX_OPTS.map((o) => {
            const on = f.existing_diagnoses.has(o.k);
            return (
              <button key={o.k} data-testid={`profile-dx-${o.k}`} onClick={() => toggleTag("existing_diagnoses", o.k)}
                style={{
                  padding: "6px 12px", borderRadius: 100, border: on ? "none" : "1px solid #D5D3C7",
                  background: on ? "#F26440" : "white", color: on ? "white" : "#333",
                  fontSize: 11.5, fontWeight: on ? 700 : 500, cursor: "pointer",
                }}>
                {on ? "✓ " : ""}{o.l}
              </button>
            );
          })}
        </div>
        <Field l="Daily prescribed medicines"><input data-testid="profile-meds" value={f.medications} onChange={(e) => setF({ ...f, medications: e.target.value })} placeholder="e.g. Telmisartan 40mg, Metformin 500mg" style={ip} /></Field>

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 13, borderRadius: 100, border: "1px solid #D5D3C7", background: "white", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button data-testid="profile-save-btn" onClick={save} disabled={saving}
            style={{ flex: 2, padding: 13, borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ children, style }) {
  return <div style={{ fontSize: 10.5, letterSpacing: 1.4, color: "#0F3D2E", fontWeight: 800, marginBottom: 10, textTransform: "uppercase", ...style }}>{children}</div>;
}
